import { useState } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { Form, Formik } from 'formik';
import * as Yup from 'yup';
import {
  CheckboxInput as CheckboxInputBase,
  Button,
  Fieldset as FieldsetBase,
  Input as InputBase,
  SliderInput as SliderInputBase,
  TableRadioInput,
  SPACINGS,
  COLORS,
  TYPOGRAPHY,
  InlineAlert as InlineAlertBase,
} from '@figure/figure-frontlib-gui';
import { numericFormatter } from '@figure/frontlib-util';
import { Card } from 'shared/components';
import * as CrossSellAppStatus from 'shared/constants/cross-sell-app-status';
import * as Consent from 'shared/constants/consent';
import { getLoanData } from 'shared/utils/offer-select-calculations';
import { offerPackageType} from 'types/offer-types';
import { personalLoanType } from 'types/personal-loan-types';
import { crossSellOfferPackageType } from 'types/cross-sell-offer-types';
import HelocDecline from './HelocDecline';
import HelocOfferCard from './HelocOfferCard';

const InlineAlert = styled(InlineAlertBase)`
  margin-bottom: ${SPACINGS.X2L}px;
`;
const Fieldset = styled(FieldsetBase)`
  :not(:last-of-type) {
    margin-bottom: ${SPACINGS.XL}px;
  }
`;

const Input = styled(InputBase)``;
const SliderInput = styled(SliderInputBase)``;

const Amount = styled.div`
  background-color: ${COLORS.NEUTRAL_100};
  padding: ${SPACINGS.XL}px;

  > h3 {
    ${TYPOGRAPHY.H3}
  }

  > p {
    ${TYPOGRAPHY.S}
  }
`;

const Row = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: ${SPACINGS.M}px;

  span {
    ${TYPOGRAPHY.M}
  }

  span + p {
    ${TYPOGRAPHY.S}
  }
`;

const TermsOfOffer = styled.div`
  > h6 {
    ${TYPOGRAPHY.H6}
    margin-bottom: ${SPACINGS.L}px;
  }

  > ${Amount} {
    margin-bottom: ${SPACINGS.XL}px;
  }
`;

const CheckboxInput = styled(CheckboxInputBase)``;
const SideContent = styled.div`
  > ${CheckboxInput}:last-of-type {
    margin-bottom: ${SPACINGS.S}px;
  }

  > p {
    ${TYPOGRAPHY.S}
    margin-bottom: ${SPACINGS.XL}px;
  }
`;

const Legal = styled.p`
  ${TYPOGRAPHY.XS}
  padding: 0 ${SPACINGS.L}px;
  :not(:last-of-type) {
    margin-bottom: ${SPACINGS.L}px;
  }
`;
export const OfferSelectV2 = ({
  offerPackage,
  personalLoan,
  handleSubmit,
  profile,
  hasSignedAppSummary,
  helocOfferPackage,
  plOfferSelected
}) => {
  const [isReset, setIsReset] = useState(false);

  const showHelocDecline = personalLoan.status?.crossSellAppStatus?.find(
    (crossSellApp) =>
      crossSellApp.crossSellAppType === CrossSellAppStatus.crossSellAppType.HELOC &&
      (crossSellApp.crossSellStatus === CrossSellAppStatus.crossSellStatus.DECLINED ||
        crossSellApp.crossSellStatus === CrossSellAppStatus.crossSellStatus.CANCELLED)
  );
  const hasHardCreditConsent =
    personalLoan.consentsList?.find((c) => c.type === Consent.HARD_CREDIT) !== undefined;

  const maxAmount = Math.floor(offerPackage.max);
  const minAmount = Math.ceil(offerPackage.min);
  const fixedOffers = offerPackage.offersByType?.find((ot) => ot.rateType === 'FIXED') || {};
  const showHelocOffer = !hasSignedAppSummary && helocOfferPackage && !plOfferSelected;

  return (
    <Formik
      initialValues={{
        amount: maxAmount,
        selectedOfferId: null,
        // Auto opt users in to autopay and credit union
        autopayOptIn: true,
        creditUnionOptIn: true,
      }}
      validationSchema={Yup.object().shape({
        selectedOfferId: Yup.string().required('Please select an offer'),
        creditUnionOptIn: Yup.bool().oneOf([true], 'Required'),
        amount: Yup.number()
          .required()
          .min(minAmount, `Must be at least ${numericFormatter.formatMoney(minAmount)}`)
          .max(maxAmount, `Must be less than ${numericFormatter.formatMoney(maxAmount)}`),
      })}
      onSubmit={(values) => {
        const selectedOffer = fixedOffers.offers?.find((o) => o.id === values.selectedOfferId);
        const autopayDiscount = selectedOffer.discountList.find(
          ({ discount }) => discount.type === 'AUTOPAY'
        );
        const selectedDiscounts = [...(values.autopayOptIn ? [autopayDiscount.discount] : [])];
        //this is used to get the correct apr!!
        const rate = values.autopayOptIn
          ? selectedOffer.rateWithAllDiscounts
          : selectedOffer.baseRate;

        const { apr, monthlyPayment } = getLoanData(
          values.amount,
          rate,
          selectedOffer.term,
          selectedOffer.originationFeePercentage
        );

        handleSubmit({
          ...(values.autopayOptIn ? { payType: 'AUTOPAY' } : {}),
          rate: selectedOffer.baseRate,
          selectedDiscounts,
          amount: values.amount,
          term: selectedOffer.term,
          rateType: selectedOffer.rateType,
          originationFeePercentage: Number(selectedOffer.originationFeePercentage),
          aprRate: apr,
          estimatedMonthlyPayment: monthlyPayment,
        });
      }}
    >
      {({ values, setFieldValue, handleSubmit }) => {
        const filteredOffers = fixedOffers.offers?.filter(
          (o) => o.min <= values.amount && o.max >= values.amount
        );
        const selectedOffer = filteredOffers.find((o) => o.id === values.selectedOfferId);

        if (selectedOffer && filteredOffers?.indexOf(selectedOffer) === -1) {
          setFieldValue('selectedOfferId', null);
          setIsReset(true);
        }

        return (
          <Form onSubmit={handleSubmit}>
            <Card
              headerCopy={
                hasHardCreditConsent
                  ? 'Select your revised offer'
                  : `Congrats${
                      profile?.name?.firstName ? `, ${profile?.name?.firstName}` : ''
                    }. You’re pre-qualified!
                You can borrow up to ${numericFormatter.formatMoney(maxAmount)}.`
              }
              customMaxWidth="110rem"
            >
              {showHelocDecline && <HelocDecline />}
              {hasHardCreditConsent && (
                <InlineAlert>
                  There was an update to your pre-qualified offer. This is due to a change in your
                  qualifications at final review. Please re-select a new offer
                </InlineAlert>
              )}

              <Fieldset
                legend="1) Select the amount of cash you want"
                helperText="Enter the specific amount or use the slider to see the terms available for your desired amount."
              >
                {/* <AmountWrapper> */}
                  <Input type="currency" name="amount" placeholder="Enter amount" />
                  <SliderInput
                    label="amount slider"
                    min={minAmount}
                    max={maxAmount}
                    name="amount"
                    step={100}
                  />
                {/* </AmountWrapper> */}
              </Fieldset>

              <Fieldset legend="2) Select your term">
                <TableRadioInput
                  name="selectedOfferId"
                  tableHeaders={['Fixed Term', 'APR', 'Est. Monthly Payment*']}
                  onChange={() => setIsReset(false)}
                  values={filteredOffers.map((o) => {
                    //TODO: Adjust based on CU/Autopay
                    const rate = values.autopayOptIn ? o.rateWithAllDiscounts : o.baseRate;
                    const { apr, monthlyPayment } = getLoanData(
                      values.amount,
                      rate,
                      o.term,
                      o.originationFeePercentage
                    );
                    return {
                      value: o.id,
                      display: [
                        `${o.term} Year`,
                        numericFormatter.formatPercentage(apr, 100, 2),
                        numericFormatter.formatMoney(monthlyPayment),
                      ],
                    };
                  })}
                />

                {isReset && (
                  <InlineAlert>
                    Rates in your state depend on the amount of cash you want to borrow. Please
                    re-select an offer to continue.
                  </InlineAlert>
                )}
              </Fieldset>

              {showHelocOffer && (
                <HelocOfferCard helocOfferPackage={helocOfferPackage} />
              )}

              <Card.SideContent>
                <SideContent>
                  <TermsOfOffer>
                    <h6>Terms of your offer:</h6>
                    <Amount>
                      <h3>{numericFormatter.formatMoney(values?.amount)}</h3>
                      <p>Cash disbursed (amount financed)</p>
                    </Amount>

                    <Row>
                      <span>One-time Origination Fee</span>
                      <span>
                        {selectedOffer
                          ? numericFormatter.formatMoney(
                              values.amount * selectedOffer.originationFeePercentage
                            )
                          : '--'}
                      </span>
                    </Row>
                    <Row>
                      <div>
                        <span>Total Loan Amount</span>
                        <p>(cash disbursed + origination fee)</p>
                      </div>
                      <span>
                        {selectedOffer
                          ? numericFormatter.formatMoney(
                              Math.round(
                                values.amount * (1 + selectedOffer.originationFeePercentage)
                              )
                            )
                          : '--'}
                      </span>
                    </Row>
                  </TermsOfOffer>

                  <hr />

                  <CheckboxInput name="autopayOptIn" vari>
                    I would like to save 0.25% interest by enrolling in AutoPay
                  </CheckboxInput>

                  <CheckboxInput helperText="" name="creditUnionOptIn">
                    By checking this box, I consent to become a member of a partner credit union as
                    required to obtain a Figure Personal Loan
                  </CheckboxInput>

                  <p>
                    Figure works with credit unions in order to offer you the best rate possible. To
                    qualify for this loan, you must opt into becoming a member of a credit union. We
                    will share your information with the credit union and provide the credit union’s
                    member agreement for you to review and sign prior to closing. If the credit
                    union purchases your loan, it will approve your membership request, fund a
                    savings account, at no cost to you, with the minimum balance needed to maintain
                    your membership, and send you a welcome letter with the terms of your account
                    and other disclosures. However, if you are not approved for membership with a
                    credit union, you will still be considered for this Figure loan. Figure will
                    remain your lender and will service your loan.
                  </p>

                  <Button useContainerWidth type="submit" disabled={!values.selectedOfferId}>
                    Continue
                  </Button>
                </SideContent>
              </Card.SideContent>

              <Card.Footer>
                <Legal>
                  You have 14 calendar days from when you submitted your basic information to
                  complete your application, including income and identity verification.
                </Legal>
                <Legal>
                  *Estimated monthly payment is based on the total loan amount (which includes the
                  applicable loan origination fee) and applicable interest charges.
                </Legal>
                <Legal>
                  This is not a commitment to lend. You may not qualify for the rates listed here.
                  If approved for a Figure Personal Loan, the fixed interest rates offered will
                  depend on your creditworthiness, and are subject to our underwriting criteria,
                  verification of your information and other terms and conditions. If you opt in to
                  AutoPay, your interest rate will include a 0.25% APR reduction once your account
                  is opened. Programs, rates, terms and conditions may change without notice. The
                  Figure Personal Loan Agreement contains other important information about the
                  loan.
                </Legal>
              </Card.Footer>
            </Card>
          </Form>
        );
      }}
    </Formik>
  );
};

OfferSelectV2.propTypes = {
  handleSubmit: PropTypes.func.isRequired,
  profile: PropTypes.shape({
    name: PropTypes.shape({
      firstName: PropTypes.string
    }),
  }).isRequired,
  plOfferSelected: PropTypes.bool.isRequired,
  offerPackage: offerPackageType.isRequired,
  personalLoan: personalLoanType.isRequired,
  helocOfferPackage: crossSellOfferPackageType,
  hasSignedAppSummary: PropTypes.bool.isRequired,
};

OfferSelectV2.defaultProps = {
  helocOfferPackage: undefined,
};